/**
 * useRelativeTime — the web relative timestamp formatter for posts, post
 * detail, the media viewer, profile and group posts, comments, replies and
 * notifications.
 *
 * Backend instants are read with `parseApiTimestamp` and the bucket comes from
 * elapsed milliseconds (`describeRelativeTime`), so the label reflects real
 * elapsed time in every viewer timezone. The shared/mobile `timeAgo` formatter
 * is deliberately untouched.
 *
 * Endpoints that answer in device time because the request carried
 * `timezoneOffset` (feed, group posts, comments, notifications) must pass
 * `naiveZone: 'local'`, matching the `timeAgo(..., 'local')` their mappers use.
 */
import { useCallback } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { parseApiTimestamp, type NaiveTimestampZone } from './apiTimestamp';
import { describeRelativeTime, formatLocalizedDate } from './relativeTime';

export interface RelativeTimeOptions {
  /** Shown when the value is missing or already a server-formatted label. */
  fallback?: string;
  /** How to read a timestamp with no timezone designator. Defaults to `'utc'`. */
  naiveZone?: NaiveTimestampZone;
}

export type RelativeTimeFormatter = (
  value: string | number | Date | null | undefined,
  options?: RelativeTimeOptions,
) => string;

export const useRelativeTime = (): RelativeTimeFormatter => {
  const { t } = useTranslation();

  return useCallback(
    (value, options = {}) => {
      const { fallback = '', naiveZone = 'utc' } = options;
      const date = parseApiTimestamp(value, naiveZone);
      if (!date) return fallback;

      const now = new Date();
      const descriptor = describeRelativeTime(date, now);

      switch (descriptor.kind) {
        case 'justNow':
          return t('relativeTime.justNow');
        case 'minutes':
          return descriptor.value === 1
            ? t('relativeTime.minuteAgo', { count: descriptor.value })
            : t('relativeTime.minutesAgo', { count: descriptor.value });
        case 'hours':
          return descriptor.value === 1
            ? t('relativeTime.hourAgo', { count: descriptor.value })
            : t('relativeTime.hoursAgo', { count: descriptor.value });
        case 'yesterday':
          return t('relativeTime.yesterday');
        case 'days':
          return t('relativeTime.daysAgo', { count: descriptor.value });
        case 'weeks':
          return descriptor.value === 1
            ? t('relativeTime.weekAgo', { count: descriptor.value })
            : t('relativeTime.weeksAgo', { count: descriptor.value });
        default:
          return formatLocalizedDate(date, now);
      }
    },
    [t],
  );
};
